import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import NProgress from 'nprogress';

/**
 * Slim top-of-viewport progress bar that animates during route navigations
 * and lazy-chunk downloads. Mounted once at app root (see main.tsx).
 *
 * Pattern (legacy React Router 6 + `<BrowserRouter>` + `<Routes>`):
 *  - Monkey-patch `history.pushState` / `replaceState` to call `NProgress.start()`
 *    synchronously at the moment of programmatic navigation.
 *  - Listen for `popstate` to catch browser back/forward.
 *  - When `useLocation().pathname` changes (the new route has actually committed),
 *    call `NProgress.done()`.
 *
 * Also exposes a tiny imperative API via the module-level `routeProgress` so
 * non-router consumers (e.g. AuthProvider during background `/me` refresh) can
 * trigger the bar without owning a hook.
 *
 * Phase UX1 T.1.
 */

// Brand cyan accent — same color as DeepSyncMark cold-boot splash.
const BAR_COLOR = '#38bdf8';

let configured = false;
function ensureConfigured() {
  if (configured) return;
  configured = true;
  NProgress.configure({
    showSpinner: false,
    trickle: true,
    trickleSpeed: 200,
    minimum: 0.08,
    easing: 'cubic-bezier(0.45, 0, 0.55, 1)',
    speed: 320,
  });

  // Inject scoped styles once. nprogress's default CSS would otherwise need to
  // be imported separately; doing it here keeps the component self-contained.
  if (typeof document !== 'undefined' && !document.getElementById('ds-nprogress-style')) {
    const style = document.createElement('style');
    style.id = 'ds-nprogress-style';
    style.textContent = `
      #nprogress { pointer-events: none; }
      #nprogress .bar {
        background: ${BAR_COLOR};
        position: fixed;
        z-index: 9999;
        top: 0; left: 0;
        width: 100%; height: 2px;
        box-shadow: 0 0 8px ${BAR_COLOR}, 0 0 4px ${BAR_COLOR};
      }
      #nprogress .peg {
        display: block;
        position: absolute;
        right: 0; width: 100px; height: 100%;
        box-shadow: 0 0 10px ${BAR_COLOR}, 0 0 5px ${BAR_COLOR};
        opacity: 1;
        transform: rotate(3deg) translate(0px, -4px);
      }
    `;
    document.head.appendChild(style);
  }
}

// Safety net — a started bar MUST always finish. DeepSync's main workspace is a
// single route (/app); portal switches change a Zustand value, not the URL, and
// same-path popstate (e.g. the map's ?hh= query states) doesn't change the
// pathname either. So start() can't rely on a pathname change to complete. Every
// start() schedules a fallback done() so the bar can never hang at ~90%.
const SAFETY_MS = 1500;
let safetyTimer: ReturnType<typeof setTimeout> | null = null;
function clearSafety() { if (safetyTimer) { clearTimeout(safetyTimer); safetyTimer = null; } }
function startGuarded() {
  ensureConfigured();
  NProgress.start();
  clearSafety();
  safetyTimer = setTimeout(() => { safetyTimer = null; NProgress.done(); }, SAFETY_MS);
}
function doneGuarded() { clearSafety(); NProgress.done(); }

/** Imperative API for non-router triggers (background fetches, portal switches). */
export const routeProgress = {
  start: startGuarded,
  done: doneGuarded,
  remove: () => { clearSafety(); NProgress.remove(); },
  /** Quick glow for instant transitions (portal switches) — start + short auto-done. */
  pulse: (ms = 450) => {
    ensureConfigured();
    NProgress.start();
    clearSafety();
    safetyTimer = setTimeout(() => { safetyTimer = null; NProgress.done(); }, ms);
  },
};

export default function RouteProgressBar() {
  const location = useLocation();

  // Patch history once on mount so programmatic + browser navigations both fire start().
  useEffect(() => {
    ensureConfigured();
    const origPush = window.history.pushState;
    const origReplace = window.history.replaceState;

    window.history.pushState = function patchedPush(...args: Parameters<typeof origPush>) {
      // Only trigger on path changes, not on hash-only or replace-state-with-same-path noise.
      const nextUrl = args[2];
      if (nextUrl && typeof nextUrl === 'string') {
        try {
          const next = new URL(nextUrl, window.location.href);
          if (next.pathname !== window.location.pathname) {
            startGuarded();
          }
        } catch {
          // ignore malformed URL — fall through
        }
      }
      return origPush.apply(this, args);
    };

    window.history.replaceState = function patchedReplace(...args: Parameters<typeof origReplace>) {
      return origReplace.apply(this, args);
    };

    const onPopState = () => { startGuarded(); };
    window.addEventListener('popstate', onPopState);

    return () => {
      window.history.pushState = origPush;
      window.history.replaceState = origReplace;
      window.removeEventListener('popstate', onPopState);
      clearSafety();
    };
  }, []);

  // Complete the bar on ANY committed navigation. `location.key` changes on every
  // push/replace/pop — including same-pathname query/hash navs that previously left
  // the bar hanging (the single-route /app bug). Pathname alone was insufficient.
  useEffect(() => {
    doneGuarded();
  }, [location.key]);

  return null;
}
